import {
  type Key,
  Text,
  Typeahead,
  TypeaheadItem,
} from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { useAtomValue, useSetAtom } from "jotai";
import {
  SyntheticEvent,
  KeyboardEvent,
  useCallback,
  useRef,
  useState,
} from "react";
import {
  Actor,
  actorsAtomFamily,
  closeSecondaryToolbarMenuSetter,
  takeSnapshotSetter,
} from "@zapier/canvas-state";
import styles from "./FloatingActorTypeahead.module.css";
import useUpdateNode from "../../../hooks/useUpdateNode";
import { generateActorColor } from "../../../utils/generateActorColor";
import { useFocusInput } from "../../../hooks/useFocusInput";
import { SelectedItem } from "../SelectedItem";
import { NodeToolbarTop } from "../NodeToolbarTop";
import { MenuWrapper } from "../MenuWrapper";

type Props = {
  actors: Actor[] | undefined;
};

export function FloatingActorTypeahead({ actors }: Props) {
  const allActors = useAtomValue(actorsAtomFamily(false));
  const inputRef = useRef<HTMLInputElement>(null);
  const takeSnapshot = useSetAtom(takeSnapshotSetter);
  const updateNodeAndSave = useUpdateNode();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const actor = actors?.[0] ?? null;
  const hasActor = actors && actors.length > 0;
  const closeSecondaryMenu = useSetAtom(closeSecondaryToolbarMenuSetter);

  useFocusInput(inputRef);

  const onInputChange = useCallback(
    (value: string) => setSearchTerm(value),
    [setSearchTerm],
  );

  const onKeyDown = useCallback(
    (event: SyntheticEvent) => {
      const keyboardEvent = event as KeyboardEvent<HTMLInputElement>;

      if (keyboardEvent.key === "Enter") {
        const trimmedSearchTerm = searchTerm.trim();
        if (trimmedSearchTerm) {
          takeSnapshot();
          const existingActorInTypeahead = allActors.find(
            (actor) => actor.label === trimmedSearchTerm,
          );

          updateNodeAndSave({
            data: {
              actors: [
                existingActorInTypeahead ?? {
                  label: trimmedSearchTerm,
                  color: actor?.color ?? generateActorColor(trimmedSearchTerm),
                },
              ],
            },
          });

          setSearchTerm("");
          closeSecondaryMenu();
        }
      }
    },
    [
      actor?.color,
      allActors,
      closeSecondaryMenu,
      searchTerm,
      takeSnapshot,
      updateNodeAndSave,
    ],
  );

  const onSelectionChange = useCallback(
    (key: Key | null) => {
      if (key) {
        takeSnapshot();
        const selectedActor = allActors.find((actor) => actor.label === key);

        if (!selectedActor) {
          return;
        }

        updateNodeAndSave({
          data: {
            actors: [selectedActor],
          },
        });

        setSearchTerm("");
        closeSecondaryMenu();
      }
    },
    [allActors, closeSecondaryMenu, takeSnapshot, updateNodeAndSave],
  );

  const onRemoveActor = useCallback(() => {
    takeSnapshot();

    updateNodeAndSave({
      data: {
        actors: undefined,
      },
    });
  }, [updateNodeAndSave, takeSnapshot]);

  return (
    <NodeToolbarTop>
      <MenuWrapper>
        {hasActor ? (
          <ul className={styles.selectedList}>
            {actors.map((actor) => (
              <li key={actor.label} className={styles.selectedItem}>
                <SelectedItem
                  label={actor.label}
                  icon={
                    <Icon
                      name="personCircleFill"
                      color={actor.color || "GrayWarm3"}
                      size={26}
                    />
                  }
                  clearSelection={onRemoveActor}
                />
              </li>
            ))}
          </ul>
        ) : (
          <div className={styles.floatingTypeaheadWrapper}>
            <Typeahead
              aria-label="Type or search people"
              placeholder="Type or search people"
              ref={inputRef}
              onKeyDown={onKeyDown}
              onInputChange={onInputChange}
              onChange={onSelectionChange}
              classNamePopOver={styles.popover}
              size="medium"
            >
              {allActors.map((actor) => {
                return (
                  <TypeaheadItem
                    key={actor.label}
                    id={actor.label}
                    textValue={actor.label}
                    className={styles.floatingTypeaheadItem}
                    size="medium"
                  >
                    <Icon
                      name="personCircleFill"
                      color={actor.color || "GrayWarm3"}
                      size={26}
                    />
                    <div className={styles.truncateText}>
                      <Text type="SmallPrint2">{actor.label}</Text>
                    </div>
                  </TypeaheadItem>
                );
              })}
            </Typeahead>
          </div>
        )}
      </MenuWrapper>
    </NodeToolbarTop>
  );
}
